import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { theme1, theme2, theme3 } from "./theme/index4";
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route, Navigate } from "react-router-dom";
import Topbar from "./components/global/Topbar";
import Sidebar, { SidebarWidth, isSideBarOpen } from "./components/global/Sidebar";
import Login from './components/auth/login';
//import Signup from './components/auth/signup';
//import ForgotPassword from './components/auth/forgotPassword';
//import ResetPassword from './components/auth/resetPassword';
import { getUserFullNameFromToken } from './components/auth/utils/auth';
import { useNavigate } from "react-router-dom";
import Dashboard from "./main/dashboard";
import Productions from "./main/productions/productions";
import ProductionCategoryItem from "./main/productions/category_item";
import { SWRConfig } from 'swr';
import api from './services/api';
import ProductionEdit from "./main/productions/productions/update";
import ProductionAdd from "./main/productions/productions/create";
import CategoryItemEdit from "./main/productions/category_item/update";
import CategoryItemAdd from "./main/productions/category_item/create";
import ProductionCategories from "./main/productions/production_categories";
import ProductionCategoriesEdit from "./main/productions/production_categories/update";
import ProductionCategoriesAdd from "./main/productions/production_categories/create";
import Expenses from "./main/productions/expenses";
import ExpensesEdit from "./main/productions/expenses/update";
import ExpensesAdd from "./main/productions/expenses/create";
import Beneficiaries from "./main/productions/beneficiaries";
import BeneficiariesEdit from "./main/productions/beneficiaries/update";
import BeneficiariesAdd from "./main/productions/beneficiaries/create";
import CategoryType from "./main/productions/category_type";
import CategoryTypeEdit from "./main/productions/category_type/update";
import CategoryTypeAdd from "./main/productions/category_type/create";
import CategoryItemType from "./main/productions/category_item_type";
import CategoryItemTypeEdit from "./main/productions/category_item_type/update";
import CategoryItemTypeAdd from "./main/productions/category_item_type/create";
import ExpenseDetailsEdit from "./main/productions/expense_details/update";
import ProductionStatusEpisode from "./main/productions/production_status";
import ProductionStatus2 from "./main/productions/production_status-2";
import ProductionStatus3 from "./main/productions/production_status-3";
import ProductionStatus4 from "./main/productions/production_status-4";
import ProductionStatus5 from "./main/productions/production_status-5";
import ProductionStatus6 from "./main/productions/production_status-6";
import ProductionStatusMonthly from "./main/productions/production_status_monthly";
import ProfitLoss from "./main/productions/profit_and_loss";
import ProductionStatusYearly from "./main/productions/productions_status_yearly";
import TopItemsProfit from "./main/productions/top_ten_items_profit";
import TopItemsLoss from "./main/productions/top_ten_items_loss";
import ProjectionsVsActual from "./main/productions/projections_vs_actual";
import ProductionStatus from "./main/productions/production_status_only";
import BeneficiaryAnalysis from "./main/productions/reports/beneficiary_analysis";
import EpisodeCostAnalysis from "./main/productions/reports/episode_cost_analysis";
import BudgetVariance from "./main/productions/reports/budget_variance";
import ExpenseTimeline from "./main/productions/reports/expense_timeline";
import CategoryPerformance from "./main/productions/reports/category_performance";
import LockedEpisodes from "./main/productions/reports/locked_episodes";
import InvoiceTracking from "./main/productions/reports/invoice_tracking";
import ProductionComparison from "./main/productions/reports/production_comparison";
import ExpenseWorkflow from "./main/productions/reports/expense_workflow";
import Recordings from "./main/productions/recordings";
import RecordingsEdit from "./main/productions/recordings/update";
import RecordingsAdd from "./main/productions/recordings/create";

const fetcher = (url) => api.get(url);
const themes = { theme1, theme2, theme3 };

// Styled Content Area Component - Must be inside ThemeProvider
const ContentAreaWrapper = ({ children, isSideBarOpen }) => {
  const theme = useTheme();
  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: isSideBarOpen ? `calc(100% - ${SidebarWidth})` : '100%',
        transition: 'width 0.3s ease',
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
};

function App() {
  const [currentTheme, setCurrentTheme] = useState('theme1');
  const [isSidebar, setIsSidebar] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userFullName, setUserFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem("token");
      const currentPath = window.location.pathname;

      const publicRoutes = ["/signup", "/forgot-password"];
      if (publicRoutes.some((route) => currentPath.startsWith(route)) && !token) {
        setLoading(false);
        return;
      }

      if (token) {
        api.post(`/auth/validate-session/`)
          .then(() => {
            setIsAuthenticated(true);
            const fullName = getUserFullNameFromToken(token);
            setUserFullName(fullName);
            //console.log("Token is valid:", token);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Token validation failed:", error);
            localStorage.removeItem("token");
            setIsAuthenticated(false);
            setLoading(false);
            navigate("/login");
          });
      } else {
        console.warn("No token found. Redirecting to login...");
        setLoading(false);
        navigate("/login");
      }
    };

    validateToken();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    navigate("/login");
  };

  if (loading) {
    return (
      <ThemeProvider theme={themes[currentTheme]}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: themes[currentTheme].palette.background.default,
          }}
        >
          <CircularProgress size={60} sx={{ color: themes[currentTheme].palette.primary.main }} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <SWRConfig value={{
      dedupingInterval: process.env.REACT_APP_BACKEND_PORT,
      fetcher,
      onError: (error) => {
        console.error("SWR error:", error);
      }
    }}>
      <ThemeProvider theme={themes[currentTheme]}>
        <CssBaseline />
        <div className="app" style={{ display: 'flex', height: '100vh' }}>
          {isAuthenticated ? (
            <>
              <Sidebar
                handleLogout={handleLogout}
              />

              {/* Content Area */}
              <ContentAreaWrapper isSideBarOpen={isSideBarOpen}>
                <Topbar isSidebar={isSideBarOpen} />
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/productions" element={<Productions />} />
                  <Route path="/productions/update/:id" element={<ProductionEdit />} />
                  <Route path="/productions/create/" element={<ProductionAdd />} />
                  <Route path="/category_item/" element={<ProductionCategoryItem />} />
                  <Route path="/category_item/update/:id" element={<CategoryItemEdit />} />
                  <Route path="/category_item/create/" element={<CategoryItemAdd />} />
                  <Route path="/production_categories/" element={<ProductionCategories />} />
                  <Route path="/production_categories/update/:id" element={<ProductionCategoriesEdit />} />
                  <Route path="/production_categories/create/" element={<ProductionCategoriesAdd />} />
                  <Route path="/expenses/" element={<Expenses />} />
                  <Route path="/expenses/update/:id" element={<ExpensesEdit />} />
                  <Route path="/expenses/create/" element={<ExpensesAdd />} />
                  <Route path="/beneficiaries/" element={<Beneficiaries />} />
                  <Route path="/beneficiaries/update/:id" element={<BeneficiariesEdit />} />
                  <Route path="/beneficiaries/create/" element={<BeneficiariesAdd />} />
                  <Route path="/category_type/" element={<CategoryType />} />
                  <Route path="/category_type/update/:id" element={<CategoryTypeEdit />} />
                  <Route path="/category_type/create/" element={<CategoryTypeAdd />} />
                  <Route path="/category_item_type/" element={<CategoryItemType />} />
                  <Route path="/category_item_type/update/:id" element={<CategoryItemTypeEdit />} />
                  <Route path="/category_item_type/create/" element={<CategoryItemTypeAdd />} />
                  <Route path="/expense_details/update/:id" element={<ExpenseDetailsEdit />} />
                  <Route path="/recordings/" element={<Recordings />} />
                  <Route path="/recordings/update/:id" element={<RecordingsEdit />} />
                  <Route path="/recordings/create/" element={<RecordingsAdd />} />
                  <Route path="/production_status/" element={<ProductionStatusEpisode />} />
                  <Route path="/production_status-2/" element={<ProductionStatus2 />} />
                  <Route path="/production_status-3/" element={<ProductionStatus3 />} />
                  <Route path="/production_status-4/" element={<ProductionStatus4 />} />
                  <Route path="/production_status-5/" element={<ProductionStatus5 />} />
                  <Route path="/production_status-6/" element={<ProductionStatus6 />} />
                  <Route path="/production_status_monthly/" element={<ProductionStatusMonthly />} />
                  <Route path="/profit_and_loss/" element={<ProfitLoss />} />
                  <Route path="/productions_status_yearly/" element={<ProductionStatusYearly />} />
                  <Route path="/top_ten_items_profit/" element={<TopItemsProfit />} />
                  <Route path="/top_ten_items_loss/" element={<TopItemsLoss />} />
                  <Route path="/projections_vs_actual/" element={<ProjectionsVsActual />} />
                  <Route path="/production_status_only/" element={<ProductionStatus />} />
                  {/* New Reports */}
                  <Route path="/reports/beneficiary-analysis/" element={<BeneficiaryAnalysis />} />
                  <Route path="/reports/episode-cost-analysis/" element={<EpisodeCostAnalysis />} />
                  <Route path="/reports/budget-variance/" element={<BudgetVariance />} />
                  <Route path="/reports/expense-timeline/" element={<ExpenseTimeline />} />
                  <Route path="/reports/category-performance/" element={<CategoryPerformance />} />
                  <Route path="/reports/locked-episodes/" element={<LockedEpisodes />} />
                  <Route path="/reports/invoice-tracking/" element={<InvoiceTracking />} />
                  <Route path="/reports/production-comparison/" element={<ProductionComparison />} />
                  <Route path="/reports/expense-workflow/" element={<ExpenseWorkflow />} />
                  {/* Admin */}

                  </Routes>
                </Box>
              </ContentAreaWrapper>
            </>
          ) : (
            <Routes>
              <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
              {/*<Route path="/signup" element={<Signup setIsAuthenticated={setIsAuthenticated} />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset/:token" element={<ResetPassword />} />*/}
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          )}
        </div>
      </ThemeProvider>

    </SWRConfig>
  );
}

export default App;
